<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

interface ErrorItem {
	row?: string | number
	message?: string
	data?: any
}
const props = withDefaults(
	defineProps<{
		modelValue: boolean
		errorMessage: string | string[] | object | null
		testMode: boolean
	}>(),
	{
		errorMessage: null,
		testMode: false
	}
)

const emit = defineEmits(['update:modelValue'])

const parsedErrors = computed<ErrorItem[]>(() => {
	if (Array.isArray(props.errorMessage)) {
		return props.errorMessage.map(error => {
			if (typeof error === 'object' && error !== null) {
				return {
					row: error.row,
					message: error.message,
					data: error.data
				} as ErrorItem
			}
			return { message: String(error) } as ErrorItem
		})
	}
	if (typeof props.errorMessage === 'object' && props.errorMessage !== null) {
		return [props.errorMessage]
	}
	if (props.errorMessage) {
		return [{ message: String(props.errorMessage) }]
	}
	return []
})

const formatData = (data: any): string => {
	try {
		if (typeof data === 'object') {
			return JSON.stringify(data, null, 2)
		}
		return String(data)
	} catch (e) {
		console.error('Error al formatear los datos:', e)
		return String(data)
	}
}

const closeModal = () => {
	emit('update:modelValue', false)
}
</script>
<template>
	<BaseModal
		:model-value="props.modelValue"
		@update:model-value="$emit('update:modelValue', $event)"
		title="Error"
		:test-mode="props.testMode"
		custom-header-class="error-modal-header"
		size="xlarge"
		message="">
		<template #body>
			<div class="text-center p-6">
				<div class="mb-6 animate-shake">
					<svg class="mx-auto" width="100" height="100" viewBox="0 0 100 100" fill="none">
						<circle cx="50" cy="50" r="48" fill="#fdecea" />
						<path d="M50 20 L82 78 H18 Z" stroke="#23272b" stroke-width="4" fill="none" />
						<circle cx="50" cy="68" r="4" fill="#23272b" />
						<rect x="46" y="40" width="8" height="18" rx="4" fill="#23272b" />
					</svg>
				</div>
				<p class="text-xl font-semibold text-gray-800 mb-4">¡Ha ocurrido un error!</p>

				<div class="max-h-[60vh] overflow-y-auto px-4">
					<div v-if="Array.isArray(parsedErrors)" class="space-y-4">
						<div v-for="(error, index) in parsedErrors" :key="index" class="bg-white rounded-lg shadow-sm border border-red-200 p-4 text-left">
							<p class="text-gray-700">{{ error.message }}</p>
							<div v-if="error.data" class="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
								<p class="font-medium mb-1">Datos:</p>
								<pre class="whitespace-pre-wrap break-words">{{ formatData(error.data) }}</pre>
							</div>
						</div>
					</div>
					<div v-else class="bg-white rounded-lg shadow-sm border border-red-200 p-4 text-left">
						<p class="text-gray-700">
							{{ props.errorMessage || 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.' }}
						</p>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-error-modal" @click="closeModal">Aceptar</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.animate-shake {
	animation: shake 0.5s;
}

@keyframes shake {
	0%,
	100% {
		transform: translateX(0);
	}
	10%,
	30%,
	50%,
	70%,
	90% {
		transform: translateX(-5px);
	}
	20%,
	40%,
	60%,
	80% {
		transform: translateX(5px);
	}
}

.btn-error-modal {
	background-color: #e53935;
	color: white;
	padding: 0.5rem 1rem;
	border-radius: 0.375rem;
	font-weight: 500;
	transition: background-color 0.2s;
}

.btn-error-modal:hover {
	background-color: #d32f2f;
}

/* Estilo para la barra de desplazamiento */
.overflow-y-auto::-webkit-scrollbar {
	width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
	background: #555;
}
</style>
